import { CATEGORY_IDS } from 'ContentUtils/constants/Categories';
import Routes from 'ContentUtils/Routes';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { Link } from 'foundations-components/transitional/components/Link';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getHasPersonalizationAccess } from '../redux/selectors/authSelectors';
import { useQuery } from 'data-fetching-client';
import { PersonalizedPagesQuery } from 'page-lib/hooks/useAllPersonalizedPagesQuery';
import { PAGE_PROPERTY_NAMES } from 'page-lib/constants/propertyNames';
import { CONTENT_PLATFORM_PROPERTY_NAME, ContentObjectTypes, getContentPropertyValue } from 'content-management-data-lib';
import { isPublishablePersonalizedPage, isUnpublishablePersonalizedPage } from 'page-lib/utils/personalization';
import useContentCrmObject from 'ContentEditorUI/components/publishing/hooks/useContentCrmObject';
import { getContentModelPersonalizationStrategyId } from '../redux/selectors/pageContentModelSelectors';
import { usePersonalizationStrategyType } from './usePersonalizationStrategyType';
import { getIsLandingPage, getIsSitePage } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
export const usePersonalizationPublishValidation = () => {
  const hasPersonalizationAccess = useSelector(getHasPersonalizationAccess);
  const personalizationStrategyIdFromContentModel = useSelector(getContentModelPersonalizationStrategyId);
  const isLandingPage = useSelector(getIsLandingPage);
  const isSitePage = useSelector(getIsSitePage);
  const contentObjectType = useMemo(() => {
    if (isLandingPage) {
      return ContentObjectTypes.LANDING_PAGE;
    }
    if (isSitePage) return ContentObjectTypes.SITE_PAGE;
    return undefined;
  }, [isLandingPage, isSitePage]);
  const strategyId = personalizationStrategyIdFromContentModel ? Number(personalizationStrategyIdFromContentModel) : undefined;
  const {
    data,
    loading,
    error
  } = useQuery(PersonalizedPagesQuery, {
    skip: !hasPersonalizationAccess || !contentObjectType,
    variables: {
      strategyId,
      contentObjectType
    }
  });
  const {
    strategyType,
    loading: strategyTypeLoading
  } = usePersonalizationStrategyType(strategyId);
  const {
    contentCrmObject
  } = useContentCrmObject();
  const {
    isPublishDisabledDueToPersonalization,
    isUnpublishDisabledDueToPersonalization,
    personalizationDefaultPageId,
    personalizationDefaultPageName
  } = useMemo(() => {
    const metaData = {
      isPublishDisabledDueToPersonalization: false,
      isUnpublishDisabledDueToPersonalization: false,
      personalizationDefaultPageId: undefined,
      personalizationDefaultPageName: undefined
    };
    const allVariants = (data === null || data === void 0 ? void 0 : data.personalizedPages.results) || [];
    if (data && !loading && !error && !strategyTypeLoading) {
      // A personalized variant should not be published if its parent is not published
      const personalizationDefault = allVariants.find(personalizedContentCrmObject => getContentPropertyValue(personalizedContentCrmObject, PAGE_PROPERTY_NAMES.PERSONALIZATION_VARIANT_STATUS) === 'DEFAULT');
      const isAbmStrategy = strategyType === 'ABM';
      metaData.isPublishDisabledDueToPersonalization = !isAbmStrategy && contentCrmObject ? !isPublishablePersonalizedPage({
        targetPage: contentCrmObject,
        allPersonalizedPagesForContentType: data === null || data === void 0 ? void 0 : data.personalizedPages
      }) : false;
      metaData.isUnpublishDisabledDueToPersonalization = contentCrmObject ? !isUnpublishablePersonalizedPage({
        targetPage: contentCrmObject,
        allPersonalizedPagesForContentType: data === null || data === void 0 ? void 0 : data.personalizedPages
      }) : false;
      metaData.personalizationDefaultPageId = personalizationDefault !== null && personalizationDefault !== void 0 && personalizationDefault.objectId ? Number(personalizationDefault.objectId) : undefined;
      metaData.personalizationDefaultPageName = personalizationDefault ? getContentPropertyValue(personalizationDefault, CONTENT_PLATFORM_PROPERTY_NAME.NAME) : undefined;
    }
    return metaData;
  }, [data, loading, error, contentCrmObject, strategyType, strategyTypeLoading]);
  const variantPublishDisabledMessage = useMemo(() => {
    if (!isPublishDisabledDueToPersonalization || !personalizationDefaultPageId || !personalizationDefaultPageName) {
      return null;
    }
    const href = Routes.edit(personalizationDefaultPageId, CATEGORY_IDS.LANDING_PAGE);
    return /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: "personalizationPublishValidation.cannotPublishVariant_jsx",
      elements: {
        Link
      },
      options: {
        linkProps: {
          href
        },
        pageName: personalizationDefaultPageName
      }
    });
  }, [isPublishDisabledDueToPersonalization, personalizationDefaultPageId, personalizationDefaultPageName]);
  return {
    isPublishDisabledDueToPersonalization,
    isUnpublishDisabledDueToPersonalization,
    isPersonalizationFetchLoading: loading || strategyTypeLoading,
    variantPublishDisabledMessage
  };
};